import { Observable, Subject, Subscription, timer } from 'rxjs';
import { tap } from 'rxjs/operators';
import WaveSurfer from 'wavesurfer.js';
import {FormArray, FormControl} from "@angular/forms";
import {required} from "../../../../../../../shared/utils";
import {ChangeDetectorRef} from "@angular/core";

declare var MediaRecorder: any;
declare const navigator: any;

export type State = 'recording' | 'stopped' | 'playing' | 'paused' | 'inactive';
export type Permissions = 'granted' | 'prompt' | 'denied' | 'unknown';

const CAN_STOP_RECORDING = ['recording'];

const CAN_START_RECORDING = ['inactive'];

const CAN_PLAY = ['inactive', 'paused'];

const CAN_PAUSE = ['playing'];

const WAVE_COLOR = '#2cc4ec';
const WAVE_PROGRESS_COLOR = '#233659';
const WAVE_BAR_WIDTH = 1;
const WAVE_CONTAINER = '#waveform';
const WAVE_HEIGHT = 20;
const WAVE_BAR_HEIGHT = 15;

interface VoiceNoteState {
  state: State;
  permission: Permissions;
  maxDuration: number;
  currentTime: number;
  $timer: Subscription;
  mediaRecorder: any;
  wavesurfer: any;
  timeSlice: number;
  blobSize: number;
  blob: any;
  downloadLink: string;
  maxBlobSize?: number;
}

export interface VoiceNoteData {
  state: State;
  currentTime: number;
  blobSize: number;
  downloadLink: string;
  permission: Permissions;
  blob: any;
  contentType: string;
}

export interface WavesurferData {
  container?: string;
  waveColor?: string;
  progressColor?: string;
  barWidth?: number;
  barHeight?: number;
}

export interface RecordingOptions {
  recorder?: boolean;
  maxDuration: number;
  timeSlice: number;
  maxBlobSize: number;
}

const TIMER = timer(0, 1000);

const MAX_DURATION_DEFAULT = 60;
const TIME_SLICE_DEFAULT = 500;
const MAX_BLOB_SIZE_DEFAULT = undefined;

export class VoiceNoteService {
  voiceNoteState: VoiceNoteState = {
    state: 'inactive',
    maxDuration: MAX_DURATION_DEFAULT,
    currentTime: 0,
    $timer: null,
    mediaRecorder: null,
    wavesurfer: null,
    timeSlice: TIME_SLICE_DEFAULT,
    blobSize: 0,
    blob: null,
    downloadLink: '',
    permission: 'unknown',
  };

  permissionSubject = new Subject<Boolean>();
  private cdr: ChangeDetectorRef;

  constructor(wavesurferData?: WavesurferData, options?: boolean | RecordingOptions, cdr?: ChangeDetectorRef) {
    this.cdr = cdr;
    this.voiceNoteState.wavesurfer = new WaveSurfer(this.getWavesurferData(wavesurferData));
    this.voiceNoteState.wavesurfer.init();

    if (this.shouldInitRecording(options)) {
      this.setRecordingOptions(options as RecordingOptions);
      this.initRecorder();
    }
  }

  get state(): State {
    return this.voiceNoteState.state;
  }

  get permission(): Permissions {
    return this.voiceNoteState.permission;
  }

  get canRecord() {
    return this.hasPermission();
  }

  get duration(): number {
    return Math.round(this.wavesurfer.getDuration());
  }

  get currentTime(): number {
    return Math.round(this.wavesurfer.getCurrentTime());
  }

  get wavesurfer() {
    return this.voiceNoteState.wavesurfer;
  }

  get timeStamp(): string {
    const currentTime = this.currentTime;

    // if (currentTime >= Math.round(this.duration) && this.voiceNoteState.state !== 'inactive') {
    //     // this.voiceNoteState.state = 'inactive';
    //     this.wavesurfer.stop();
    // }

    if (currentTime === 0) {
      return this.timeConvertor(this.duration);
    } else {
      return this.timeConvertor(currentTime);
    }
  }

  set state(state: State) {
    this.voiceNoteState.state = state;
  }

  private timeConvertor(timeInSec: number): string {
    const minutes = Math.floor(timeInSec / 60);
    const seconds = timeInSec - minutes * 60;

    return `${this.timeOutput(minutes, '0', 2)}:${this.timeOutput(seconds, '0', 2)}`;
  }

  private timeOutput(string, pad, length) {
    return (new Array(length + 1).join(pad) + string).slice(-length);
  }

  get stateTypes() {
    return {
      CAN_STOP_RECORDING: CAN_STOP_RECORDING,
      CAN_START_RECORDING: CAN_START_RECORDING,
      CAN_PLAY: CAN_PLAY,
      CAN_PAUSE: CAN_PAUSE,
    };
  }

  get voiceNotData(): VoiceNoteData {
    return {
      blobSize: this.voiceNoteState.blobSize,
      currentTime: this.voiceNoteState.currentTime,
      downloadLink: this.voiceNoteState.downloadLink,
      state: this.voiceNoteState.state,
      permission: this.voiceNoteState.permission,
      blob: this.voiceNoteState.blob,
      contentType: 'audio/ogg',
    };
  }

  set setPermissionData(permission: Boolean) {
    this.permissionSubject.next(permission);
  }

  get getPermissionData(): Observable<Boolean> {
    return this.permissionSubject;
  }

  public startRecording() {
    if (CAN_START_RECORDING.includes(this.voiceNoteState.state)) {
      this.startTimer();
      this.voiceNoteState.mediaRecorder.start(this.voiceNoteState.timeSlice);
      this.voiceNoteState.state = 'recording';
    }
  }

  public stopRecording() {
    if (
      CAN_STOP_RECORDING.includes(this.voiceNoteState.state) &&
      this.voiceNoteState.mediaRecorder
    ) {
      this.stopTimer();
      this.voiceNoteState.mediaRecorder.stop();
      this.voiceNoteState.state = 'inactive';
    }
  }

  public play() {
    if (CAN_PLAY.includes(this.voiceNoteState.state)) {
      this.voiceNoteState.state = 'playing';
      this.wavesurfer.play();
    }
  }

  public pause() {
    if (CAN_PAUSE.includes(this.voiceNoteState.state)) {
      this.voiceNoteState.state = 'paused';
      this.wavesurfer.pause();
    }
  }

  public download(media: any) {
    this.wavesurfer.load(media);
  }

  public clear() {
    this.wavesurfer.destroy();
    this.voiceNoteState = null;
  }

  private initRecorder() {
    this.hasPermissions();
    navigator.mediaDevices
      .getUserMedia({ audio: true, video: false })
      .then(this.handleSuccess)
      .catch((error) => console.warn(error.message));
  }

  private handleSuccess = (stream) => {
    // Chrome only supports audio/webm
    let options = { mimeType: 'audio/webm;codecs=opus' };

    if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
      options = { mimeType: 'audio/ogg;codecs=opus' };
    }

    const recordedChunks = [];
    this.voiceNoteState.mediaRecorder = new MediaRecorder(stream, options);

    this.voiceNoteState.mediaRecorder.ondataavailable = (chunk) => {
      if (chunk.data.size > 0) {
        recordedChunks.push(chunk.data);
      }
      if (this.maxBlobSize() || this.maxDuration()) {
        this.stopRecording();
      }

      const blob = new Blob(recordedChunks, { type: 'audio/ogg; codecs=opus' });
      this.voiceNoteState.blobSize = blob.size;
      this.voiceNoteState.blob = blob;
      this.download(URL.createObjectURL(blob));
    };

    this.voiceNoteState.mediaRecorder.onstop = () => {
      const blob = new Blob(recordedChunks, { type: 'audio/ogg; codecs=opus' });
      if (blob) {
        this.voiceNoteState.downloadLink = URL.createObjectURL(blob);
      }
    };

    this.startRecording();
  };

  private hasPermission(): boolean {
    navigator.permissions.query({ name: 'microphone' }).then((result) => {
      this.voiceNoteState.permission = result.state;
      this.setPermissionData = result.state === 'granted';
    });
    return this.voiceNoteState.permission === 'granted';
  }

  private hasPermissions = () => {
    navigator.permissions
      .query({ name: 'microphone' })
      .then((result) => {
        this.voiceNoteState.permission = result.state;
        this.setPermissionData = result.state === 'granted';
        result.onchange = (event) => {
          this.voiceNoteState.permission = event.currentTarget.state;
          this.setPermissionData = result.state === 'granted';
        };
      })
      .catch((error) => console.warn(error.message));
  };

  private startTimer() {
    this.voiceNoteState.$timer = TIMER.pipe(
      tap((time) => (this.voiceNoteState.currentTime = time)),
      tap(() => this.cdr?.markForCheck()),
      tap((time) => {
        if (time === this.voiceNoteState.maxDuration) {
          this.stopRecording();
        }
      })
    ).subscribe();
  }

  private stopTimer() {
    this.voiceNoteState.$timer.unsubscribe();
  }

  private getWavesurferData(wavesurferData: WavesurferData): any {
    return {
      container: WAVE_CONTAINER,
      waveColor: WAVE_COLOR,
      progressColor: WAVE_PROGRESS_COLOR,
      barWidth: WAVE_BAR_WIDTH,
      height: WAVE_HEIGHT,
      barHeight: WAVE_BAR_HEIGHT,
      ...wavesurferData,
    };
  }

  private setRecordingOptions(options: RecordingOptions) {
    if (!options) {
      return;
    }
    this.voiceNoteState.maxDuration = options.maxDuration
      ? options.maxDuration
      : MAX_DURATION_DEFAULT;
    this.voiceNoteState.maxBlobSize = options.maxDuration
      ? options.maxDuration
      : MAX_BLOB_SIZE_DEFAULT;
    this.voiceNoteState.timeSlice = options.timeSlice ? options.timeSlice : TIME_SLICE_DEFAULT;
  }

  private maxBlobSize(): boolean {
    return (
      this.voiceNoteState.maxBlobSize &&
      this.voiceNoteState.maxBlobSize === this.voiceNoteState.blobSize
    );
  }

  private maxDuration(): boolean {
    return this.duration && this.duration >= this.voiceNoteState.maxDuration;
  }

  private shouldInitRecording(options: boolean | RecordingOptions): boolean {
    if (typeof options === 'object') {
      return options.recorder ? options.recorder : false;
    }
    return options ? options : false;
  }
}
